import React, { Fragment, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { QueryStringContext } from 'contexts/QueryStringContext';
import InitiativeBar from './InitiativeBar';
import {
	getAdditionalHeader,
	getHeaders,
	getMinMaxInitiativesDates,
	getPeriods,
	getDayWidth,
	getParametersForRange,
	getStartOfPeriod,
	getEndOfPeriod,
} from './utils';
import {
	WAVE_PANELS_MARGIN,
	INITIATIVE_HEIGHT,
	INITIATIVE_MARGIN,
	PERIODS,
} from '../constants';
import {
	Container,
	ContentHeader,
	DraggableContainer,
	Header,
	PeriodHeader,
	PeriodHeaderTitle,
	PeriodHeaderValue,
	PeriodHeaderLine,
	PeriodHover,
	PeriodColumn,
	PeriodLine,
	SubHeader,
	TodayContainer,
	TodayTopCircle,
	TodayLine,
} from './CalendarContainer.styles';

const CalendarContainer = ({
	calendarContainerRef,
	initiatives,
	updateInitiativeDuration,
	waveInitiatives,
	expandedWaves,
	searchInitiative,
	rightContainerWidth,
}) => {
	const [hoveredPeriod, setHoveredPeriod] = useState(null);
	const { period } = useContext(QueryStringContext);

	useEffect(
		() => {
			const {
				minDate: minInitiativeDate,
				maxDate: maxInitiativeDate,
			} = getMinMaxInitiativesDates(initiatives);
			setMinDate(getStartOfPeriod(minInitiativeDate, period));
			setMaxDate(getEndOfPeriod(maxInitiativeDate, period));
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[initiatives]
	);

	const {
		minDate: minInitiativeDate,
		maxDate: maxInitiativeDate,
	} = getMinMaxInitiativesDates(initiatives);
	const [minDate, setMinDate] = useState(
		getStartOfPeriod(minInitiativeDate, period)
	);
	const [maxDate, setMaxDate] = useState(
		getEndOfPeriod(maxInitiativeDate, period)
	);
	const dayWidth = getDayWidth(period);

	let headers = getHeaders(minDate, maxDate, period);
	let calendarWidth = headers.reduce((acc, header) => {
		return acc + header.duration * dayWidth;
	}, 0);
	while (calendarWidth < rightContainerWidth) {
		headers = getAdditionalHeader(headers, period);
		calendarWidth = headers.reduce((acc, header) => {
			return acc + header.duration * dayWidth;
		}, 0);
	}
	let lastDate = maxDate;
	if (headers.length) {
		const lastHeader = headers[headers.length - 1]
		lastDate = moment(lastHeader.dateStart).add(lastHeader.duration, 'days');
	}
	const periods = getPeriods(minDate, lastDate, period);

	const todayLeft = moment().diff(moment(minDate), 'days');

	const barsData = waveInitiatives.reduce(
		(acc, { initiatives, wave }, index) => {
			const {
				minDate: waveMinDate,
				maxDate: waveMaxDate,
			} = getMinMaxInitiativesDates(initiatives);
			const isWaveFaded =
				searchInitiative && searchInitiative.waveId !== wave.id;
			const summaryBarParams = {
				...getParametersForRange({
					minDate,
					startDate: waveMinDate,
					endDate: waveMaxDate,
				}),
				executionStartDate: waveMinDate.toISOString(),
				duration: Math.round(
					moment
						.duration(moment(waveMaxDate).diff(moment(waveMinDate)))
						.asWeeks()
				),
				top:
					(index + 1) * WAVE_PANELS_MARGIN +
					acc.length * (INITIATIVE_HEIGHT + INITIATIVE_MARGIN),
				isSummary: true,
				isFaded: isWaveFaded,
			};
			const isExpanded = expandedWaves[wave.id];
			const initiativesParams = (isExpanded ? initiatives : []).map(
				(initiative, index) => ({
					...initiative,
					isFaded: searchInitiative && searchInitiative.id !== initiative.id,
					top:
						summaryBarParams.top +
						(index + 1) * (INITIATIVE_HEIGHT + INITIATIVE_MARGIN),
				})
			);
			acc = [...acc, summaryBarParams, ...initiativesParams];

			return acc;
		},
		[]
	);

	const isAdditionalHoverBlocks = period === PERIODS[0] && periods.length;
	let areaHeight = 0;
	if (barsData && barsData.length) {
		areaHeight = barsData[barsData.length - 1].top + INITIATIVE_HEIGHT;
	}

	const isHorizontalScroll = Boolean(
		calendarContainerRef &&
			calendarContainerRef.current &&
			calendarContainerRef.current.scrollWidth
	);
	const calendarAreaHeight = areaHeight;

	return (
		<Container>
			<Header width={calendarWidth}>
				<ContentHeader>
					{headers.map(({ duration, title, value }) => (
						<PeriodHeader
							key={`${minDate}-${title}-${value}-${duration}`}
							width={duration * dayWidth}
						>
							<PeriodHeaderTitle>{title}</PeriodHeaderTitle>
							<PeriodHeaderValue>{value}</PeriodHeaderValue>
							<PeriodHeaderLine
								height={calendarAreaHeight}
								additionalHeight={!isHorizontalScroll}
							/>
						</PeriodHeader>
					))}
				</ContentHeader>
				<SubHeader onMouseOut={() => setHoveredPeriod(null)}>
					<TodayContainer left={todayLeft * dayWidth}>
						<TodayTopCircle />
						<TodayLine height={calendarAreaHeight} />
					</TodayContainer>
					{periods.map(({ left, title, duration, periodLeft }, index) => {
						if (title === 1) {
							return null;
						}
						const leftMinus =
							period === PERIODS[0] ? (dayWidth * duration) / 2 : 0;
						return (
							<Fragment key={`${minDate}-${title}-${left}`}>
								<PeriodColumn
									left={left * dayWidth - leftMinus}
									width={duration * dayWidth}
									highlight={hoveredPeriod === index}
								>
									{title}
								</PeriodColumn>
								<PeriodLine
									highlight={period === PERIODS[0] && hoveredPeriod === index}
									left={periodLeft * dayWidth - 1}
									additionalHeight={!isHorizontalScroll}
								/>
							</Fragment>
						);
					})}
				</SubHeader>
			</Header>
			<DraggableContainer innerRef={calendarContainerRef} width={calendarWidth}>
				{isAdditionalHoverBlocks ? (
					<PeriodHover
						left={(-dayWidth * periods[0].duration) / 2}
						width={periods[0].duration * dayWidth}
						height={calendarAreaHeight}
						additionalHeight={!isHorizontalScroll}
					/>
				) : null}
				{periods.map(({ left, title, duration }, index) => {
					const leftMinus =
						period === PERIODS[0] ? (dayWidth * duration) / 2 : 0;
					return (
						<PeriodHover
							key={`${minDate}-${title}-${left}`}
							left={left * dayWidth - leftMinus}
							width={duration * dayWidth}
							onMouseOver={() => setHoveredPeriod(index)}
							onMouseOut={() => setHoveredPeriod(null)}
							height={calendarAreaHeight}
							additionalHeight={!isHorizontalScroll}
						/>
					);
				})}
				{isAdditionalHoverBlocks ? (
					<PeriodHover
						left={
							(periods[periods.length - 1].left +
								periods[periods.length - 1].duration / 2) *
							dayWidth
						}
						width={periods[periods.length - 1].duration * dayWidth}
						height={calendarAreaHeight}
						additionalHeight={!isHorizontalScroll}
					/>
				) : null}
				{barsData.map((bar, index) => (
					<InitiativeBar
						minDate={minDate}
						maxDate={maxDate}
						setMinDate={setMinDate}
						setMaxDate={setMaxDate}
						period={period}
						dayWidth={dayWidth}
						key={`${index}-${bar.isSummary ? '!' : bar.id}-${bar.duration}-${
							bar.executionStartDate
						}`}
						initiative={bar}
						updateInitiativeDuration={updateInitiativeDuration}
					/>
				))}
			</DraggableContainer>
		</Container>
	);
};

export default CalendarContainer;
